"use client";

import { useState, useEffect } from "react";
import { motion } from "motion/react";
import { Eye, EyeOff, ArrowRight, Globe, ChevronDown } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useLang } from "@/context/lang";
import type { Lang } from "@/context/lang";
import { Logo } from "@/components/logo";
import CreatenanoMark from "@/components/createnano-mark";
import { useTheme } from "@/components/theme-provider";

const CREATENANO_BASE =
  process.env.NEXT_PUBLIC_CREATENANO_BASE_URL || "https://createnano.com";

const CREATENANO_SSO_START = `${CREATENANO_BASE}/sso/start?audience=speakroar`;

const LANGS: { code: Lang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "tr", label: "Türkçe" },
  { code: "es", label: "Español" },
  { code: "bs", label: "Bosanski" },
  { code: "ru", label: "Русский" },
  { code: "de", label: "Deutsch" },
];

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const router = useRouter();
  const { T, lang, setLang } = useLang();
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  const isDark = mounted && resolvedTheme === "dark";

  const apiUrl = process.env.NEXT_PUBLIC_API_URL || "https://studio.speakroar.com";

  // URL'den reset token kontrol et
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("reset");
    if (token) {
      setResetToken(token);
      setResetMode(true);
    }
  }, []);

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setResetLoading(true);
    setError("");
    try {
      const resp = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetToken, password: newPassword }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        setResetDone(true);
      } else {
        setError(data.error || "Reset failed");
      }
    } catch {
      setError("Connection error");
    }
    setResetLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) { setError(T("auth.email_required") || "Email required"); return; }
    setForgotLoading(true);
    setError("");
    try {
      await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setForgotSent(true);
    } catch {
      setError(T("auth.connection_error") || "Connection error");
    }
    setForgotLoading(false);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const resp = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        localStorage.setItem("speakroar-user", JSON.stringify(data.user));
        const params = new URLSearchParams(window.location.search);
        const redirect = params.get("redirect") || "/dashboard";
        router.push(redirect);
      } else {
        setError(data.error || T("auth.invalid_creds"));
        setLoading(false);
      }
    } catch {
      setError(T("auth.connection_error"));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden bg-gradient-to-br from-cyan-50/40 via-white to-cyan-50/30 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950">
      {/* Ambient animated background (behind everything) */}
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <motion.div
          className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-cyan-300/30 dark:bg-cyan-500/10 blur-[120px]"
          animate={{ x: [0, 30, 0], y: [0, -20, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/3 -left-20 w-[600px] h-[600px] rounded-full bg-blue-300/25 dark:bg-blue-500/10 blur-[120px]"
          animate={{ x: [0, -25, 0], y: [0, 20, 0], scale: [1, 0.95, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-40 left-1/4 w-[480px] h-[480px] rounded-full bg-purple-300/20 dark:bg-purple-500/10 blur-[120px]"
          animate={{ x: [0, 20, 0], y: [0, -15, 0], scale: [1, 1.05, 1] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md bg-white/80 dark:bg-white/5 backdrop-blur-xl rounded-3xl ring-1 ring-neutral-200/70 dark:ring-white/10 shadow-2xl shadow-cyan-500/10 dark:shadow-black/30 p-8 sm:p-10"
        >
          <Link href="/" className="inline-block mb-8">
            <Logo size="lg" white={isDark} />
          </Link>

          {resetMode ? (
            <>
              <h1 className="text-3xl font-black tracking-tight mb-2"><span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 dark:from-cyan-300 dark:via-sky-400 dark:to-purple-400 bg-clip-text text-transparent">{"New Password"}</span></h1>
              <p className="text-neutral-500 dark:text-slate-400 text-sm mb-8 font-medium">Enter your new password below.</p>

              {resetDone ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 text-center">
                    <div className="text-green-600 dark:text-emerald-300 font-bold text-sm mb-1">Password updated!</div>
                    <div className="text-green-700/70 dark:text-emerald-300/70 text-xs">You can now log in with your new password.</div>
                  </div>
                  <button
                    onClick={() => { setResetMode(false); setResetDone(false); router.replace("/login"); }}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 via-sky-500 to-teal-500 text-white font-bold text-sm hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
                  >
                    {T("auth.login")} <ArrowRight size={16} />
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 dark:text-slate-300 mb-2 tracking-wide uppercase">New Password</label>
                    <div className="relative">
                      <input
                        type={showPass ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/5 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-slate-500 text-sm focus:border-cyan-500 focus:bg-white dark:focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/15 outline-none transition-all pr-10"
                        placeholder="Min 8 characters"
                        minLength={8}
                      />
                      <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-slate-500 hover:text-cyan-600 dark:hover:text-cyan-300 transition-colors">
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300 text-sm font-semibold">{error}</div>
                  )}

                  <button
                    onClick={handleResetPassword}
                    disabled={resetLoading}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 via-sky-500 to-teal-500 text-white font-bold text-sm hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
                  >
                    {resetLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>Update Password <ArrowRight size={16} /></>
                    )}
                  </button>
                </div>
              )}
            </>
          ) : forgotMode ? (
            <>
              <h1 className="text-3xl font-black tracking-tight mb-2"><span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 dark:from-cyan-300 dark:via-sky-400 dark:to-purple-400 bg-clip-text text-transparent">{T("auth.forgot") || "Forgot Password"}</span></h1>
              <p className="text-neutral-500 dark:text-slate-400 text-sm mb-8 font-medium">
                {T("auth.forgot_desc") || "Enter your email and we'll send you a reset link."}
              </p>

              {forgotSent ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl bg-green-50 border border-green-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 text-center">
                    <div className="text-green-600 dark:text-emerald-300 font-bold text-sm mb-1">{T("auth.reset_sent") || "Reset link sent!"}</div>
                    <div className="text-green-700/70 dark:text-emerald-300/70 text-xs">{T("auth.check_email") || "Check your email inbox and click the reset link."}</div>
                  </div>
                  <button
                    onClick={() => { setForgotMode(false); setForgotSent(false); }}
                    className="w-full py-3 rounded-xl border border-neutral-200 bg-white text-neutral-700 font-semibold text-sm hover:bg-neutral-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
                  >
                    {T("auth.back_to_login") || "Back to Login"}
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 dark:text-slate-300 mb-2 tracking-wide uppercase">{T("auth.email")}</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/5 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-slate-500 text-sm focus:border-cyan-500 focus:bg-white dark:focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/15 outline-none transition-all"
                      placeholder="you@example.com"
                      required
                    />
                  </div>

                  {error && (
                    <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300 text-sm font-semibold">{error}</div>
                  )}

                  <button
                    onClick={handleForgotPassword}
                    disabled={forgotLoading}
                    className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 via-sky-500 to-teal-500 text-white font-bold text-sm hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
                  >
                    {forgotLoading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>{T("auth.send_reset") || "Send Reset Link"} <ArrowRight size={16} /></>
                    )}
                  </button>

                  <button
                    onClick={() => { setForgotMode(false); setError(""); }}
                    className="w-full py-2 text-sm text-neutral-500 hover:text-neutral-700 dark:text-slate-400 dark:hover:text-white font-medium transition-colors"
                  >
                    {T("auth.back_to_login") || "Back to Login"}
                  </button>
                </div>
              )}
            </>
          ) : (
            <>
              <h1 className="text-3xl font-black tracking-tight mb-2"><span className="bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 dark:from-cyan-300 dark:via-sky-400 dark:to-purple-400 bg-clip-text text-transparent">{T("auth.login")}</span></h1>
              <p className="text-neutral-500 dark:text-slate-400 text-sm mb-8 font-medium">
                {T("auth.login_desc")}
              </p>

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-xs font-bold text-neutral-700 dark:text-slate-300 mb-2 tracking-wide uppercase">{T("auth.email")}</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/5 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-slate-500 text-sm focus:border-cyan-500 focus:bg-white dark:focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/15 outline-none transition-all"
                    placeholder="you@example.com"
                    required
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-bold text-neutral-700 dark:text-slate-300 tracking-wide uppercase">{T("auth.password")}</label>
                    <button type="button" onClick={() => { setForgotMode(true); setError(""); setForgotSent(false); }} className="text-xs text-cyan-600 dark:text-cyan-300 hover:text-cyan-700 dark:hover:text-cyan-200 font-medium bg-transparent border-none cursor-pointer">{T("auth.forgot")}</button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/5 text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-slate-500 text-sm focus:border-cyan-500 focus:bg-white dark:focus:bg-white/10 focus:ring-2 focus:ring-cyan-500/15 outline-none transition-all pr-10"
                      placeholder="••••••••"
                      required
                    />
                    <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-slate-500 hover:text-cyan-600 dark:hover:text-cyan-300 transition-colors">
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300 text-sm font-semibold">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-cyan-500 via-sky-500 to-teal-500 text-white font-bold text-sm hover:shadow-xl hover:shadow-cyan-500/40 hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>{T("auth.login")} <ArrowRight size={16} /></>
                  )}
                </button>
              </form>

              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-neutral-200 dark:bg-white/10" />
                <span className="text-[10px] font-semibold text-neutral-400 tracking-widest">OR</span>
                <div className="flex-1 h-px bg-neutral-200 dark:bg-white/10" />
              </div>

              <a
                href={CREATENANO_SSO_START}
                className="w-full flex items-center justify-center gap-2.5 py-3.5 rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/5 text-neutral-900 dark:text-white font-semibold text-sm hover:bg-neutral-50 hover:border-neutral-400 dark:hover:bg-white/10 dark:hover:border-white/20 transition-colors no-underline"
              >
                <CreatenanoMark size={20} className="text-neutral-900" />
                Continue with{" "}
                <span style={{ fontFamily: "'Nexa', 'Nunito', sans-serif" }}>
                  <span style={{ fontWeight: 300 }}>create</span>
                  <span style={{ fontWeight: 900 }}>nano</span>
                </span>
              </a>
            </>
          )}

          <p className="text-sm text-neutral-500 dark:text-slate-400 text-center mt-6 font-medium">
            {T("auth.no_account")}{" "}
            <Link href="/register" className="text-cyan-600 dark:text-cyan-300 font-bold hover:text-cyan-700 dark:hover:text-cyan-200 transition-colors">{T("auth.register")}</Link>
          </p>

          <div className="flex items-center justify-center mt-6 relative">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-1.5 text-xs text-neutral-400 dark:text-slate-500 hover:text-neutral-600 dark:hover:text-slate-300 font-medium transition-colors"
            >
              <Globe size={12} />
              {LANGS.find(l => l.code === lang)?.label}
              <ChevronDown size={10} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute bottom-full mb-1 bg-white dark:bg-slate-900 rounded-xl border border-neutral-200 dark:border-white/10 shadow-xl py-1 w-32 z-50">
                {LANGS.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm font-medium transition-colors ${lang === l.code ? "text-cyan-600 bg-cyan-50 dark:text-cyan-300 dark:bg-cyan-500/10" : "text-neutral-600 hover:bg-neutral-50 dark:text-slate-300 dark:hover:bg-white/5"}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Right - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-cyan-500 via-sky-500 to-teal-600 items-center justify-center p-12 relative overflow-hidden">
        {/* Animated waveform stripes */}
        <div className="absolute inset-0 opacity-10">
          {Array.from({ length: 20 }).map((_, i) => {
            const seed1 = ((i * 7 + 3) % 40);
            const seed2 = ((i * 13 + 5) % 30);
            const seed3 = ((i * 11 + 2) % 30);
            const seed4 = ((i * 17 + 7) % 40);
            const seed5 = ((i * 19 + 1) % 30);
            return (
              <motion.div
                key={i}
                className="absolute w-1 bg-white rounded-full"
                style={{ left: `${5 + i * 5}%`, height: `${30 + seed1}%`, top: `${20 + seed2}%` }}
                animate={{ height: [`${20 + seed3}%`, `${40 + seed4}%`, `${20 + seed5}%`] }}
                transition={{ duration: 2, repeat: Infinity, delay: i * 0.1 }}
              />
            );
          })}
        </div>
        {/* Floating glass orbs */}
        <motion.div
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/10 blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/10 blur-3xl"
          animate={{ scale: [1, 1.15, 1], opacity: [0.25, 0.4, 0.25] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative z-10 text-center text-white max-w-md"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 ring-1 ring-white/20 backdrop-blur-sm text-[10px] font-black tracking-widest mb-6">
            ✨ STUDIO ACCESS
          </div>
          <h2 className="text-4xl font-black mb-5 leading-tight tracking-tight">
            {T("hero.studio_visual_title")}
          </h2>
          <p className="text-white/85 text-base leading-relaxed mb-8">
            {T("hero.studio_visual_desc")}
          </p>
          {/* Feature chips */}
          <div className="flex flex-wrap justify-center gap-2">
            {["2,600+ Voices", "25+ Languages", "Voice Cloning", "Video Dubbing", "Audio Editor"].map((f, i) => (
              <motion.span
                key={f}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + i * 0.08, duration: 0.4 }}
                className="px-3 py-1.5 rounded-full bg-white/15 ring-1 ring-white/20 backdrop-blur-sm text-xs font-semibold"
              >
                {f}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
